/**
 * Le code di qTournaments (1.72.0, ADR-0163) viste dal telefono: coda 1v1 e sit&go sono UN motore.
 *
 * Il giocatore entra con un **biglietto** (uno per gioco), lo **rinnova** ogni `renewEveryMs` mentre
 * l'app e' in primo piano, e un giro del motore ogni 4 s forma i gruppi e crea un torneo gia' pieno
 * che parte subito. Da li' e' un torneo normale: check-in dell'incontro, partita, referto.
 *
 * 📜 Contratto: kb#282 (@gionko, 2026-09-25) — le capability `tournament.queue.list|join|leave|renew`,
 * le forme `Coda` e `Biglietto`, il vocabolario dei rifiuti proposto in `details.queueReason`.
 *
 * 🔴 **Le rotte le dichiara qlash-limulo#146, e le serve lo staging da qlash-limulo#151.** #146
 * (`5119d3b`) aggiunge a `app-qlash-api/backend/endpoint-catalog.js` `GET /v1/tournaments/queues` e
 * `POST /v1/tournaments/queues/{join,leave,renew}`, gli stessi path e metodi di `QUEUE_ROUTES`; #151
 * (`7e0eae2`, qTournaments 1.75.1) le porta in servizio [M 2026-09-26: la lista risponde 200].
 * Fino al 2026-09-25 erano una proposta su kb#282 (issuecomment-5836785751). Se cambiano, si cambiano
 * QUI, e solo qui (`verify-limulo.ts` lo pretende).
 *
 * ⚠️ Puro per costruzione: niente `import.meta.env`, niente orologio implicito. Lo esegue anche
 * `tools/verify-limulo.ts` sotto Node.
 */

import type { LimuloMatch } from '../../types/limulo.ts';
import { matchStateOf, winnerSideOf } from './match.ts';
import { isTournamentTerminal } from './polling.ts';
import type { Presentazione } from './presentazione.ts';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Le rotte — in un punto solo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ `list` e' un letterale sotto `/v1/tournaments`: nel BFF va montato PRIMA di
 * `/v1/tournaments/:tournamentId`, o `queues` diventa l'id di un torneo (lo stesso ordine che
 * qTournaments usa per le sue `/queues`, `tavola.ts`).
 */
export const QUEUE_ROUTES = {
  list: '/v1/tournaments/queues',
  join: '/v1/tournaments/queues/join',
  leave: '/v1/tournaments/queues/leave',
  renew: '/v1/tournaments/queues/renew',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Le forme — lette al bordo, mai credute
// ─────────────────────────────────────────────────────────────────────────────

export type LimuloQueueKind = 'coda' | 'sit-and-go';

export interface LimuloQueue {
  queueId: string;
  name: string;
  /** `coda` = 1v1; `sit-and-go` = da 3 a 32. Un terzo valore rende la voce illeggibile, non «1v1». */
  kind: LimuloQueueKind;
  size: number;
  /** `single-elimination` | `swiss` | `round-robin`, passato cosi' com'e' (il motore puo' aggiungerne). */
  format: string | null;
  gameId: string;
  gameModeKey: string | null;
  /** I minuti per il check-in dell'incontro, dopo l'abbinamento. `null` = non dichiarato. */
  matchCheckInMinutes: number | null;
  noShowCooldownMinutes: number | null;
  state: string;
  /** Quanti aspettano adesso. `null` = il server non l'ha detto, che e' diverso da zero. */
  waiting: number | null;
  /**
   * Quanti tornei questo modello ha fatto partire, da sempre (i gruppi `started` e `closed`,
   * `elencaCode` di qTournaments 1.75.1). `null` = non detto: non si stampa «0 partiti».
   */
  groupsStarted: number | null;
}

export type LimuloTicketState = 'waiting' | 'matched' | 'idle';

/**
 * Perche' il biglietto e' chiuso. `altro` e' un motivo che il contratto di oggi non nomina: si
 * dice «non sei piu' in coda» invece di tacere, e non si indovina quale sia.
 *
 * `sfida`: il giocatore e' entrato in una SFIDA e il motore lo toglie dalla coda del gioco, cosi' non
 * puo' essere abbinato a una seconda partita mentre gioca la prima. Il valore e' esattamente `'sfida'`
 * [M `qlash-limulo@staging` 9f077b8, `app-qtournaments/app/core/code.js:696`, qTournaments 1.89.0, kb#296].
 */
export type LimuloTicketExit =
  | 'abbinato'
  | 'uscita'
  | 'rinnovo-scaduto'
  | 'coda-in-pausa'
  | 'sfida'
  | 'altro';

export interface LimuloQueueTicket {
  gameId: string;
  state: LimuloTicketState;
  /** Le code in cui si aspetta, con da quando (epoch ms; `null` se illeggibile). */
  queues: { queueId: string; since: number | null }[];
  /** Il gruppo mentre il torneo si prepara (pochi secondi). */
  matched: { queueId: string | null; at: number | null } | null;
  /** Il torneo nato dall'ultimo abbinamento: quello in cui entrare. */
  lastMatch: { queueId: string | null; tournamentId: string; at: number | null } | null;
  exitReason: LimuloTicketExit | null;
  exitAt: number | null;
  renewEveryMs: number | null;
  expiresAfterMs: number | null;
}

export interface LimuloQueueList {
  queues: LimuloQueue[];
  tickets: LimuloQueueTicket[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const nonEmpty = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Un istante: il contratto dice epoch ms. Una stringa ISO si accetta al bordo (la stessa unione
 * che il resto della sezione ammette solo qui), tutto il resto e' `null` — mai `0`, che a schermo
 * diventerebbe «in coda da 56 anni».
 */
export function instantOf(value: unknown): number | null {
  const n = finite(value);
  if (n !== null) return n > 0 ? n : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Una `Coda`, o `null` se manca quello senza cui non si puo' ne' mostrarla ne' entrarci. */
export function queueOf(raw: unknown): LimuloQueue | null {
  const r = asRecord(raw);
  const queueId = nonEmpty(r.queueId);
  const gameId = nonEmpty(r.gameId);
  const kind = r.kind === 'coda' || r.kind === 'sit-and-go' ? r.kind : null;
  const size = finite(r.size);
  if (queueId === null || gameId === null || kind === null || size === null || size < 2)
    return null;
  return {
    queueId,
    name: nonEmpty(r.name) ?? queueId,
    kind,
    size,
    format: nonEmpty(r.format),
    gameId,
    gameModeKey: nonEmpty(r.gameModeKey),
    matchCheckInMinutes: finite(r.matchCheckInMinutes),
    noShowCooldownMinutes: finite(r.noShowCooldownMinutes),
    state: nonEmpty(r.state) ?? 'unknown',
    waiting: finite(r.waiting),
    groupsStarted: finite(r.groupsStarted),
  };
}

const EXITS: readonly LimuloTicketExit[] = [
  'abbinato',
  'uscita',
  'rinnovo-scaduto',
  'coda-in-pausa',
  'sfida',
];

/**
 * Un `Biglietto`, o `null` se non si sa in che stato sia. ⛔ Uno `state` sconosciuto NON diventa
 * `idle`: «sei fuori» detto a chi e' ancora in coda gli fa perdere il posto al prossimo rinnovo
 * mancato — meglio «non leggibile», che la schermata dice come tale.
 */
export function ticketOf(raw: unknown): LimuloQueueTicket | null {
  const r = asRecord(raw);
  const gameId = nonEmpty(r.gameId);
  const state =
    r.state === 'waiting' || r.state === 'matched' || r.state === 'idle' ? r.state : null;
  if (gameId === null || state === null) return null;

  const queues = (Array.isArray(r.queues) ? r.queues : [])
    .map((q) => {
      const rec = asRecord(q);
      const queueId = nonEmpty(rec.queueId);
      return queueId === null ? null : { queueId, since: instantOf(rec.since) };
    })
    .filter((q): q is { queueId: string; since: number | null } => q !== null);

  const m = r.matched === null || r.matched === undefined ? null : asRecord(r.matched);
  const lm = asRecord(r.lastMatch);
  const lastTournament = nonEmpty(lm.tournamentId);
  const exitRaw = nonEmpty(r.exitReason);

  return {
    gameId,
    state,
    queues,
    matched: m === null ? null : { queueId: nonEmpty(m.queueId), at: instantOf(m.at) },
    lastMatch:
      lastTournament === null
        ? null
        : { queueId: nonEmpty(lm.queueId), tournamentId: lastTournament, at: instantOf(lm.at) },
    exitReason: exitRaw === null ? null : (EXITS.find((e) => e === exitRaw) ?? 'altro'),
    exitAt: instantOf(r.exitAt),
    renewEveryMs: finite(r.renewEveryMs),
    expiresAfterMs: finite(r.expiresAfterMs),
  };
}

/** La risposta di `tournament.queue.list`: le voci illeggibili si scartano una per una. */
export function queueListOf(raw: unknown): LimuloQueueList {
  const r = asRecord(raw);
  const pick = <T>(list: unknown, of: (x: unknown) => T | null): T[] =>
    (Array.isArray(list) ? list : []).map(of).filter((x): x is T => x !== null);
  return { queues: pick(r.queues, queueOf), tickets: pick(r.tickets, ticketOf) };
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. I corpi delle tre scritture
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Entrare: in un modello, o in tutti quelli attivi di un gioco («qualsiasi modalita'»).
 *
 * ⚠️ `profileId`, NON l'oggetto `profile` del contratto: e' la stessa strada dell'iscrizione
 * (`registrationBody`, kb#237) — il telefono nomina il SUO profilo, e il BFF lo compone e ne
 * verifica la proprieta' (`pickProfileId`, `qlash-limulo#118`). Un `profile` scritto dal client
 * sarebbe un profilo che il client puo' inventare. Anche questo e' su kb#282 come richiesta.
 */
export type QueueJoinInput =
  | { queueId: string; profileId?: string | null }
  | { gameId: string; anyMode: true; profileId?: string | null };

export function queueJoinBody(input: QueueJoinInput): Record<string, unknown> {
  const profilo = (input.profileId ?? '').trim();
  const base: Record<string, unknown> =
    'queueId' in input
      ? { queueId: input.queueId.trim() }
      : { gameId: input.gameId.trim(), anyMode: true };
  return profilo === '' ? base : { ...base, profileId: profilo };
}

/** Uscire: da un modello, o — senza `queueId` — da tutte le code di quel gioco. */
export function queueLeaveBody(input: {
  gameId: string;
  queueId?: string | null;
}): Record<string, unknown> {
  const queueId = (input.queueId ?? '').trim();
  return queueId === ''
    ? { gameId: input.gameId.trim() }
    : { gameId: input.gameId.trim(), queueId };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Il momento del biglietto — cosa dire al giocatore
// ─────────────────────────────────────────────────────────────────────────────

export type LimuloQueueMoment =
  /** Nessun biglietto, o chiuso senza un motivo da dire. */
  | { kind: 'fuori' }
  | { kind: 'in-attesa'; queues: { queueId: string; since: number | null }[] }
  /** Abbinato: il torneo si sta creando. Il torneo non si conosce ancora. */
  | { kind: 'abbinato' }
  /** Il torneo c'e': si va all'INCONTRO, dove c'e' il check-in (entro `matchCheckInMinutes`). */
  | { kind: 'partita-trovata'; tournamentId: string; queueId: string | null; at: number | null }
  /** Uscite: `uscita` e' voluta; `rinnovo-scaduto`, `coda-in-pausa` e `sfida` NON sono errori, sono frasi. */
  | { kind: 'uscito'; reason: Exclude<LimuloTicketExit, 'abbinato'>; at: number | null };

/**
 * 🔴 `lastMatch` NON basta a dire «partita trovata»: resta sul biglietto anche dopo, quando si e'
 * usciti per altre ragioni (una coda nuova, una pausa). Decide `exitReason === 'abbinato'`, e
 * `lastMatch` dice soltanto DOVE andare. Senza il torneo, un abbinato resta «si sta preparando».
 */
export function queueMomentOf(ticket: LimuloQueueTicket | null): LimuloQueueMoment {
  if (ticket === null) return { kind: 'fuori' };
  if (ticket.state === 'waiting') return { kind: 'in-attesa', queues: ticket.queues };
  if (ticket.state === 'matched') return { kind: 'abbinato' };
  switch (ticket.exitReason) {
    case 'abbinato':
      return ticket.lastMatch === null
        ? { kind: 'abbinato' }
        : {
            kind: 'partita-trovata',
            tournamentId: ticket.lastMatch.tournamentId,
            queueId: ticket.lastMatch.queueId,
            at: ticket.exitAt ?? ticket.lastMatch.at,
          };
    case null:
      return { kind: 'fuori' };
    default:
      return { kind: 'uscito', reason: ticket.exitReason, at: ticket.exitAt };
  }
}

/**
 * 🔴 «Partita trovata» sul biglietto NON basta quando il TORNEO e' finito: il motore tiene
 * `exitReason: 'abbinato'` e `lastMatch` PER SEMPRE (li cambia solo un join nuovo), e senza
 * guardare il torneo la schermata resterebbe su «Partita trovata» a torneo concluso — persino
 * dopo un reload, che riadotta lo stesso biglietto idle/abbinato. `isTournamentTerminal`
 * (`./polling.ts`) e' la STESSA enumerazione dello storico: una seconda qui divergerebbe.
 *
 * ⚠️ Fail-closed: `tournament` non arrivato (in lettura) o con una lettura fallita e' `undefined`
 * per chi chiama, e `isTournamentTerminal(undefined)` e' `false` — resta «partita-trovata». Non
 * si offre un rientro che il motore rifiuterebbe con `queue.in-game`.
 *
 * `eliminato` (`eliminatoDalTorneo`, qui sotto) e' l'altra uscita dello stesso predicato del motore:
 * a torneo vivo, chi e' fuori dal tabellone rientra in coda (L-2436).
 */
export function queueMomentWithTournament(
  momento: LimuloQueueMoment,
  tournament: unknown,
  eliminato = false,
): LimuloQueueMoment {
  if (momento.kind !== 'partita-trovata') return momento;
  if (isTournamentTerminal(tournament)) return { kind: 'fuori' };
  return eliminato ? { kind: 'fuori' } : momento;
}

/**
 * 🔑 **«Partita trovata» ⇔ il motore rifiuterebbe il rientro con `queue.in-game`** (L-2436, il
 * «partita trovata» fantasma dell'hub). Il predicato e' quello del motore, `bloccoDiRientro`
 * (`app-qtournaments/app/core/code.js:579-599` @ qlash-limulo `4348dc6`): si e' «in gioco» finche'
 * il gruppo e' formato o partito — qui: il torneo NON e' terminale, `queueMomentWithTournament` —
 * e, SOLO nei formati che eliminano (`single-elimination`), finche' non si e' PERSO un incontro
 * concluso. Negli altri formati (svizzero, girone) si gioca fino in fondo e si rientra a torneo
 * chiuso. Questa funzione e' la seconda meta': `true` solo quando l'eliminazione e' CERTA.
 *
 * - Il formato e' quello della coda che ha fatto nascere il torneo (`lastMatch.queueId`): il BFF
 *   non lo porta sul torneo, e una 1v1 e' `single-elimination` per definizione (motore, `code.js`
 *   `:117-143`). Ignoto (coda non piu' in lista) ⇒ `false`.
 * - «Perso» = referto DEFINITIVO (`resultType` in una lista BIANCA: `normal`, `forfeit`), stato
 *   `decided`, e il vincitore NON e' il mio lato. Un `resultType` che non conosco e' un «non lo so»,
 *   e non elimina (revisione di #496, 🟡 2: `decided` da solo era una lista NERA). Il mio lato lo
 *   dice solo `isMe`: assente su entrambi i lati ⇒ quell'incontro non conta. `GET …/matches` senza
 *   `scope` rende GIA' i soli miei (default `mine`: `incontri.js:648` @ qlash-limulo `4348dc6`; KB
 *   `services/qlash-limulo/api-client.md:177`), e la TMA non manda `scope`: gli incontri altrui qui
 *   non arrivano, e i rami che li scartano sono DIFENSIVI (per un bordo che un giorno li portasse).
 * - 🛡️ **Un incontro MIO ancora aperto** (`pending`/`proposed`/`disputed`, con `isMe === true` su un
 *   lato: DICHIARATO mio, non «forse mio») ⇒ in corsa, QUALUNQUE cosa dica il formato (revisione di
 *   #496, 🟡 3). Il «forse mio» riportava la carta di L-2436 col bottone verso un incontro ALTRUI; il
 *   «dichiarato mio», quando sbaglia, da' «fuori» con «Entra» e il rifiuto col link: ha un'uscita. Il formato e' quello della
 *   coda di OGGI, non del gruppo: se lo staff porta un modello da svizzero a eliminazione diretta,
 *   la TMA leggerebbe «eliminato» mentre il motore ha ancora turni da farmi giocare. Con un incontro
 *   aperto non si sbaglia; fra due turni, senza incontri aperti, resta il caso scoperto: «fuori»,
 *   con «Entra» — e il rifiuto `queue.in-game` col link al torneo. L'incontro del turno nuovo
 *   rimette la carta: da eliminati gli incontri si rileggono ancora, a 30 s (`useCodeTorneiPannello`).
 * - Ogni «non lo so» e' `false` ⇒ resta «partita-trovata»: fail-closed come il torneo non letto.
 */
export function eliminatoDalTorneo(
  format: string | null | undefined,
  matches: readonly LimuloMatch[] | null | undefined,
): boolean {
  if (!Array.isArray(matches)) return false;
  const dichiaratoMio = (m: LimuloMatch) => m.players.a?.isMe === true || m.players.b?.isMe === true;
  if (matches.some((m) => INCONTRO_APERTO.has(matchStateOf(m)) && dichiaratoMio(m))) return false;
  if (format !== 'single-elimination') return false;
  return matches.some((m) => {
    if (!RISULTATI_DEFINITIVI.has(m.result?.resultType ?? '')) return false;
    if (matchStateOf(m) !== 'decided') return false;
    const mio = m.players.a?.isMe === true ? 'a' : m.players.b?.isMe === true ? 'b' : null;
    return mio !== null && winnerSideOf(m) !== mio;
  });
}

/** I referti che chiudono DAVVERO un incontro, per l'eliminazione: lista bianca (🟡 2 di #496). */
const RISULTATI_DEFINITIVI: ReadonlySet<string> = new Set(['normal', 'forfeit']);
/** Un incontro ancora da giocare o da chiudere (gli stessi di `matchToOpenOf`). */
const INCONTRO_APERTO: ReadonlySet<string> = new Set(['pending', 'proposed', 'disputed']);

// ─────────────────────────────────────────────────────────────────────────────
// 5. Il ritmo del rinnovo
// ─────────────────────────────────────────────────────────────────────────────

/** Il contratto dice ~20 s: vale solo se il server non ne manda uno leggibile. */
export const RENEW_FALLBACK_MS = 20_000;

/**
 * Da abbinati il torneo nasce in «pochi secondi» (kb#282): aspettare 20 s per saperlo vuol dire
 * bruciare un terzo di minuto dei cinque del check-in. Si rilegge piu' spesso SOLO in quel momento.
 */
export const RENEW_WHILE_MATCHED_MS = 3_000;

/** Sotto il secondo e' un valore guasto, non un ritmo: meglio il ripiego che un ciclo stretto. */
const RENEW_MIN_MS = 1_000;

/** Fra quanto rinnovare, o `null` se il biglietto e' chiuso e il rinnovo si ferma. */
export function renewDelayOf(ticket: LimuloQueueTicket): number | null {
  if (ticket.state === 'idle') return null;
  if (ticket.state === 'matched') return RENEW_WHILE_MATCHED_MS;
  const ms = ticket.renewEveryMs;
  return ms !== null && ms >= RENEW_MIN_MS ? ms : RENEW_FALLBACK_MS;
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. I rifiuti — il vocabolario proposto su kb#282
// ─────────────────────────────────────────────────────────────────────────────

/**
 * I motivi che il BFF mette in `details.queueReason` (proposta di @gionko su kb#282; il
 * vocabolario pubblico e' di @phomea). ⚠️ Copia dell'allowlist del server
 * (`app-qlash-api/backend/queue-refusal.js` @ `qlash-limulo` `7e0eae2`: gli stessi 10, riletti il
 * 2026-09-26). Se il server la cambia, si rilegge QUESTA lista contro la sua.
 */
export const QUEUE_REASONS = [
  'queue.not-found',
  'queue.none-for-game',
  'queue.paused',
  'queue.profile-required',
  'queue.profile-rejected',
  'queue.already-matched',
  'queue.in-game',
  'queue.cooldown',
  'queue.not-waiting',
  'queue.busy',
] as const;

export type LimuloQueueReason = (typeof QUEUE_REASONS)[number];

export interface LimuloQueueRefusal {
  reason: LimuloQueueReason;
  /** Solo per `queue.cooldown`: quando si rientra (epoch ms). `null` ⇒ la frase senza l'ora. */
  finoA: number | null;
  /** Per `queue.in-game` e `queue.cooldown`: il torneo da finire. `null` ⇒ niente scorciatoia. */
  tournamentId: string | null;
}

/**
 * Il rifiuto nominato di una scrittura sulle code, o `null`.
 *
 * Legge SOLO `data.details`, mai il messaggio. Lo stato ammesso e' quello del contratto (404, 409,
 * 422): lo stesso cancello largo di `registrationReasonOf` — la guardia vera e' il vocabolario chiuso.
 * `finoA` e `tournamentId` li manda gia' il motore (`core/code.ts:127-128`, dentro `esiti[0]`); se
 * il BFF li butta, qui arrivano `null` e la frase resta generica: non si inventa un'ora.
 */
export function queueRefusalOf(error: unknown): LimuloQueueRefusal | null {
  const envelope = asRecord(error);
  if (envelope.status !== 404 && envelope.status !== 409 && envelope.status !== 422) return null;
  const details = asRecord(asRecord(envelope.data).details);
  const reason = QUEUE_REASONS.find((known) => known === details.queueReason);
  if (reason === undefined) return null;
  return {
    reason,
    finoA: reason === 'queue.cooldown' ? instantOf(details.finoA) : null,
    tournamentId: nonEmpty(details.tournamentId),
  };
}

/** La frase di ogni motivo (namespace `limulo`). */
export const QUEUE_REASON_COPY: Record<LimuloQueueReason, string> = {
  'queue.not-found': 'queues.reason.notFound',
  'queue.none-for-game': 'queues.reason.noneForGame',
  'queue.paused': 'queues.reason.paused',
  'queue.profile-required': 'queues.reason.profileRequired',
  'queue.profile-rejected': 'queues.reason.profileRejected',
  'queue.already-matched': 'queues.reason.alreadyMatched',
  'queue.in-game': 'queues.reason.inGame',
  'queue.cooldown': 'queues.reason.cooldown',
  'queue.not-waiting': 'queues.reason.notWaiting',
  'queue.busy': 'queues.reason.busy',
};

/**
 * `queue.in-game` — «hai ancora una partita aperta» — dice cio' che quella partita E': una partita (coda, e anche quando non
 * si sa ancora, r15), un sit&go, o un torneo vero. `QUEUE_REASON_COPY['queue.in-game']` e' quello del torneo.
 */
export const QUEUE_IN_GAME_COPY: Record<Presentazione, string> = {
  coda: 'queues.reason.inGameMatch',
  'sit-and-go': 'queues.reason.inGameSitngo',
  torneo: 'queues.reason.inGame',
  // La sfida e' un torneo da 2: il testo resta quello del torneo (il comportamento di prima di r15-bis).
  sfida: 'queues.reason.inGame',
};

/** Il freno CON l'ora: «puoi rientrare alle {{time}}». Senza `finoA` si usa la frase del motivo. */
export const QUEUE_COOLDOWN_UNTIL_COPY = 'queues.reason.cooldownUntil';

/** Le uscite dal biglietto che si dicono al giocatore (namespace `limulo`). */
export const QUEUE_EXIT_COPY: Record<Exclude<LimuloTicketExit, 'abbinato'>, string> = {
  uscita: 'queues.exit.left',
  'rinnovo-scaduto': 'queues.exit.renewExpired',
  'coda-in-pausa': 'queues.exit.paused',
  sfida: 'queues.exit.challenge',
  altro: 'queues.exit.other',
};

/**
 * La chiave della frase di un rifiuto, con l'ora del freno quando c'e'. ⚠️ `queue.not-waiting` su
 * un'uscita NON e' un fallimento: si era gia' fuori. Lo decide chi chiama (`leftAlready`).
 */
export function queueRefusalCopyOf(
  refusal: LimuloQueueRefusal,
  presentazione: Presentazione | null = null,
): {
  key: string;
  finoA: number | null;
} {
  if (refusal.reason === 'queue.cooldown' && refusal.finoA !== null) {
    return { key: QUEUE_COOLDOWN_UNTIL_COPY, finoA: refusal.finoA };
  }
  if (refusal.reason === 'queue.in-game') return { key: QUEUE_IN_GAME_COPY[presentazione ?? 'coda'], finoA: null };
  return { key: QUEUE_REASON_COPY[refusal.reason], finoA: null };
}

/** Un'uscita rifiutata perche' non si era in coda e' un'uscita riuscita. */
export const leftAlready = (refusal: LimuloQueueRefusal | null): boolean =>
  refusal?.reason === 'queue.not-waiting';

// ─────────────────────────────────────────────────────────────────────────────
// 7. Quali code sono di quale gioco, e quale incontro aprire
// ─────────────────────────────────────────────────────────────────────────────

/** Il `profileType` di Clash Royale nel catalogo giochi (`GET /v1/games`). */
export const CR_PROFILE_TYPE = 'clash-royale';

/**
 * Il nome che si dice a schermo per i giochi che hanno le code (`{{game}}` dei testi `queues.sitngo.*`).
 * Un gioco che non e' qui non ha il pannello: una voce in piu' e' una promessa in piu'.
 */
export const CODE_GAME_NAMES: Readonly<Record<string, string>> = Object.freeze({
  [CR_PROFILE_TYPE]: 'Clash Royale',
  backgammon: 'Backgammon',
});

/**
 * I `gameId` del catalogo che hanno uno dei `profileType` dati, o `null` se il catalogo non c'e' o non ne ha.
 * ⚠️ Il `gameId` di una coda e' lo stesso di un torneo — un id DELL'ORGANIZZAZIONE (`"1"` su
 * staging), non il nome del gioco: il nome sta in `profileType` (v. il cappello di `openGame.ts`).
 * Il `"clash-royale"` negli esempi di kb#282 e' illustrativo. Stessa regola di `backgammonGameIdsOf`.
 */
export function gameIdsOfProfileTypes(
  games: readonly { gameId: string; profileType: string }[] | null | undefined,
  profileTypes: readonly string[],
): Set<string> | null {
  if (!Array.isArray(games)) return null;
  const ids = games
    .filter((g) => profileTypes.includes(g.profileType) && g.gameId !== '')
    .map((g) => g.gameId);
  return ids.length > 0 ? new Set(ids) : null;
}

/** I `gameId` del catalogo di UN gioco (v. `gameIdsOfProfileTypes`). */
export function gameIdsOfProfileType(
  games: readonly { gameId: string; profileType: string }[] | null | undefined,
  profileType: string,
): Set<string> | null {
  return gameIdsOfProfileTypes(games, [profileType]);
}

/** I `gameId` del catalogo che sono Clash Royale: il caso di `gameIdsOfProfileType` che le code hanno da sempre. */
export function crGameIdsOf(
  games: readonly { gameId: string; profileType: string }[] | null | undefined,
): Set<string> | null {
  return gameIdsOfProfileType(games, CR_PROFILE_TYPE);
}

/**
 * Il biglietto che la barra e `CodeTorneiValue.ticket` raccontano, fra quelli di piu' giochi: l'ultimo toccato
 * da chi scrive SE e' ancora in attesa o abbinato; altrimenti il primo in attesa o abbinato della mappa;
 * altrimenti l'ultimo toccato (chiuso, che la barra tace); altrimenti `null`.
 * 🔴 Un biglietto chiuso toccato per ultimo NON copre un'attesa viva di un altro gioco: quella rinnova, e la
 * barra deve dirla. Con UN gioco solo e' sempre quel biglietto, com'e' sempre stato.
 */
export function ticketToTell(
  tickets: Readonly<Record<string, LimuloQueueTicket>>,
  ultimo: string | null,
): LimuloQueueTicket | null {
  const toccato = ultimo === null ? undefined : tickets[ultimo];
  if (toccato !== undefined && toccato.state !== 'idle') return toccato;
  return Object.values(tickets).find((t) => t.state !== 'idle') ?? toccato ?? null;
}

/**
 * Il biglietto di UNO di questi giochi, fra quelli che l'app tiene (uno per gioco, come il server:
 * `biglietto.model.ts` di qTournaments). `null` se i giochi non sono noti (catalogo non letto) o non c'e'.
 * Se per assurdo ce ne fosse piu' d'uno (due `gameId` dello stesso `profileType`), vince quello in attesa.
 * 🔴 Serve a ogni pannello delle code: un biglietto di un altro gioco non e' «la tua attesa» qui.
 */
export function ticketOfGames(
  tickets: Readonly<Record<string, LimuloQueueTicket>>,
  gameIds: ReadonlySet<string> | null,
): LimuloQueueTicket | null {
  if (gameIds === null) return null;
  const suoi = Object.values(tickets).filter((t) => gameIds.has(t.gameId));
  return suoi.find((t) => t.state !== 'idle') ?? suoi[0] ?? null;
}

/**
 * I biglietti che la barra racconta: UNA riga per gioco in attesa o abbinato (r14: «in attesa in piu' giochi, una
 * riga per gioco»). Prima la barra ne raccontava uno solo (`ticketToTell`): con due giochi in coda «Esci» usciva da
 * quello e l'altro rinnovava senza riga.
 * · i biglietti vivi (`state !== 'idle'`) sono tutti, nell'ordine della mappa;
 * · `ticket` (quello che `ticketToTell` rende) entra se e' vivo e la mappa non lo porta (chi non ha la mappa);
 * · senza nessun biglietto vivo resta `ticket` da solo, anche chiuso (il raro idle/abbinato senza torneo, che
 *   `rigaCodaOf` sa raccontare): con UN gioco e' sempre quel biglietto, com'e' sempre stato.
 */
export function ticketsToTell(
  tickets: Readonly<Record<string, LimuloQueueTicket>>,
  ticket: LimuloQueueTicket | null,
): LimuloQueueTicket[] {
  const vivi = Object.values(tickets).filter((t) => t.state !== 'idle');
  if (ticket !== null && ticket.state !== 'idle' && !vivi.some((t) => t.gameId === ticket.gameId)) {
    vivi.push(ticket);
  }
  if (vivi.length > 0) return vivi;
  return ticket === null ? [] : [ticket];
}

/** La lista ristretta ai giochi dati: le code e i biglietti di quei giochi e basta. */
export function queueListForGames(list: LimuloQueueList, gameIds: ReadonlySet<string>): LimuloQueueList {
  return {
    queues: list.queues.filter((q) => gameIds.has(q.gameId)),
    tickets: list.tickets.filter((t) => gameIds.has(t.gameId)),
  };
}

/**
 * Le code di un tipo solo. 🔴 La «Ranked» tiene le `coda` (1v1) e il pannello «Sit&Go» — a flag
 * acceso, `SitngoCodePanel` (`VITE_FEATURE_TOURNAMENT_SITNGO_ENABLED`) — i `sit-and-go`: la vetrina di `tournament.queue.list` li manda TUTTI insieme (un modello sit&go e'
 * «un tipo di modello della stessa coda», qTournaments 1.72.0), e senza questo filtro il primo
 * sit&go attivato dallo staff comparirebbe nella Ranked.
 * ⚠️ I biglietti NON si filtrano: sono uno per gioco (`biglietto.model.ts`), e una stessa attesa
 * puo' stare in una 1v1 e in un sit&go insieme.
 */
export function queuesOfKind(
  queues: readonly LimuloQueue[],
  kind: LimuloQueueKind,
): LimuloQueue[] {
  return queues.filter((q) => q.kind === kind);
}

/**
 * «Entra in tutte le code» (r14, Luca 05/10: «un bottone entra in tutte le code»; «appena parte una coda e accetti,
 * lui droppa le altre»): si offre quando il gioco ha ALMENO DUE modelli attivi in vetrina, di qualunque tipo
 * (1v1 e sit&go). Con uno solo il bottone farebbe quello che fa la riga: non serve.
 * 🔴 Manda `anyMode: true` SENZA `kind`: il motore prende TUTTI i modelli attivi del gioco (`core/code.js`
 * `entraInCoda`, `qualsiasi`), sit&go compresi, e al primo abbinamento esce da tutte le altre attese DELLO STESSO
 * gioco, nella stessa prenotazione atomica (ADR-0163 §7: il multi-table non e' dichiarato da nessun gioco,
 * fail-closed). Il `kind` (qT 1.77.0, `capability/code.js`) lo restringerebbe a un tipo: qui NON si manda, perche'
 * «tutte» vuol dire tutte (decisione di tma-orcb sul dispatch r14, fase 2).
 * ⚠️ `queues` sono quelle di UN gioco e della vetrina, che mostra solo le `active` (`vetrinaCode`): le stesse che
 * `anyMode` prenderebbe. Una voce buttata al bordo da `queueOf` (un terzo `kind` futuro) qui non conta, ma il
 * motore la prenderebbe.
 * ⚠️ Giochi DIVERSI restano indipendenti (§7): un bottone per gioco, mai uno che attraversa i giochi.
 * Col biglietto: se si aspetta gia' in TUTTI i modelli non c'e' niente da aggiungere, e il bottone si toglie
 * (dal «Sit&Go» l'elenco resta anche in attesa; la «Ranked» non mostra l'elenco ad attesa in corso).
 */
export function tutteLeCodeSiOffrono(
  queues: readonly LimuloQueue[],
  ticket: LimuloQueueTicket | null = null,
): boolean {
  if (queues.length < 2) return false;
  return !queues.every((q) => waitingInQueue(ticket, q.queueId));
}

/** La frase di ogni formato che il motore dichiara oggi (namespace `limulo`); un formato nuovo non si stampa. */
export const QUEUE_FORMAT_COPY: Readonly<Record<string, string>> = {
  'single-elimination': 'queues.ui.format.singleElimination',
  swiss: 'queues.ui.format.swiss',
  'round-robin': 'queues.ui.format.roundRobin',
};

/**
 * Si aspetta in QUESTO modello: il biglietto e' in attesa e lo nomina fra le sue code. Da abbinati
 * no: il torneo sta nascendo, e da li' non si esce (il motore risponde `queue.already-matched`).
 */
export function waitingInQueue(ticket: LimuloQueueTicket | null, queueId: string): boolean {
  return ticket?.state === 'waiting' && ticket.queues.some((q) => q.queueId === queueId);
}

/**
 * Da quanto si aspetta: dal primo `since` leggibile (si puo' aspettare in piu' code, e conta la
 * piu' vecchia). `null` = nessun istante leggibile, e allora il tempo NON si mostra — un contatore
 * che parte da zero a ogni apertura direbbe «hai appena cominciato» a chi aspetta da minuti.
 */
export function waitingSinceOf(queues: readonly { since: number | null }[]): number | null {
  const istanti = queues.map((q) => q.since).filter((s): s is number => s !== null);
  return istanti.length === 0 ? null : Math.min(...istanti);
}

/** `m:ss` (o `h:mm:ss`) di una durata; negativa = 0 (un orologio del telefono avanti non va in rosso). */
export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/**
 * L'incontro in cui portare il giocatore appena abbinato: il primo ancora DA GIOCARE fra i suoi
 * (`GET …/matches` e' gia' «i miei», scope `mine`). `null` = il torneo non ha ancora incontri
 * leggibili (nasce in pochi secondi): chi chiama porta alla lista degli incontri del torneo, che
 * si rilegge da sola, e non alla scheda del torneo — li' il check-in d'incontro non si trovava
 * (reperto di Luca del 2026-09-25).
 */
export function matchToOpenOf<M extends { matchId: string }>(
  matches: readonly M[] | null | undefined,
  stateOf: (m: M) => string,
): M | null {
  if (!Array.isArray(matches)) return null;
  const daGiocare = new Set(['pending', 'proposed', 'disputed']);
  return matches.find((m) => daGiocare.has(stateOf(m))) ?? null;
}
